import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock3 } from "lucide-react";
import { getIntegrations } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { fiRelative } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { integrationFreshness, useFreshnessClock } from "@/lib/sourceFreshness";

export const DataFreshnessNotice = ({ integrationId, sourceLabel, coverageComplete }) => {
  const { data, loading, error, refetch } = useData(getIntegrations, [], ["integrations"]);
  const now = useFreshnessClock();
  const integration = (data || []).find((item) => item.id === integrationId);

  if (loading) return <div className="mb-4 h-12 animate-pulse rounded-xl border border-border bg-muted/40" />;
  if (error) return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-500/25 bg-rose-500/5 px-3 py-2 text-xs">
      <span className="flex items-center gap-2 text-rose-300"><AlertTriangle className="h-4 w-4" /> {sourceLabel}-yhteyden tilaa ei voitu tarkistaa.</span>
      <button type="button" onClick={refetch} className="font-semibold text-foreground">Yritä uudelleen</button>
    </div>
  );

  const connected = integration?.status === "connected";
  const state = integrationFreshness(integration, now);
  const fresh = connected && state === "fresh" && coverageComplete !== false;
  const stale = connected && state === "stale";
  const Icon = fresh ? CheckCircle2 : stale ? Clock3 : AlertTriangle;
  const message = connected && coverageComplete === false
    ? `${sourceLabel}: haun kattavuutta ei ole vahvistettu. Tarkista käsittelytilanne alta.`
    : connected && ["future", "unknown"].includes(state)
    ? `${sourceLabel}: päivitysajankohta on virheellinen tai tulevaisuudessa. Tietojen tuoreutta ei voida varmistaa.`
    : fresh
    ? `${sourceLabel} päivitetty ${fiRelative(integration.last_sync)}.`
    : stale
      ? `${sourceLabel}-tiedot voivat olla vanhentuneita. Viimeisin päivitys ${fiRelative(integration.last_sync)}.`
      : connected
        ? `${sourceLabel}-yhteys toimii, mutta onnistunutta päivitystä ei ole vielä kirjattu.`
        : `${sourceLabel}-yhteyttä ei ole vielä vahvistettu. Näkyvät tiedot voivat olla paikallisia tai vanhoja.`;

  return (
    <div className={cn("mb-4 flex items-start justify-between gap-3 rounded-xl border px-3 py-2 text-xs", fresh ? "border-emerald-500/25 bg-emerald-500/5" : "border-amber-500/25 bg-amber-500/5")} data-testid={`freshness-${integrationId}`}>
      <span className={cn("flex items-start gap-2", fresh ? "text-emerald-300" : "text-amber-300")}><Icon className="mt-0.5 h-4 w-4 shrink-0" /> {message}</span>
      {!fresh && <Link to="/settings" className="shrink-0 font-semibold text-foreground">Asetukset</Link>}
    </div>
  );
};
